import { UserType } from "interfaces";
type EventKeyPath =
  | "categories"
  | "createEventInfo"
  | "updateEventInfo"
  | "getTemplates"
  | "createTemplate"
  | "getEventDropdown"
  | "getAttendantTemplateByEventId"
  | "getAttendantTemplateByTemplateIds"
  | "getParticipantAttendantTemplate"
  | "updateTemplate"
  | "getTemplateLog"
  | "getAdditionalSetupData"
  | "createAdditionalSetup"
  | "updateAdditionalSetup"
  | "getTickets"
  | "getPhases"
  | "toggleTicketPhaseStatus"
  | "toggleGroupTicketStatus"
  | "togglePublishPhasesStatus"
  | "createTicketPhase"
  | "updateTicketPhase"
  | "createParentTicket"
  | "createSingleTicket"
  | "createTicketByType"
  | "updateParentTicket"
  | "updateSingleTicket"
  | "reorderTicket"
  | "createSubTicket"
  | "updateSubTicket"
  | "getTicketHistoryLog"
  | "getTicketPhaseHistoryLog";

type PromotionKeyPath =
  | "getConditionDropdown"
  | "getConditionDetail"
  | "getConditionDropdown2"
  | "getPromotionEventPercentFixedRuleDropdown"
  | "getPromotionEventDropdown"
  | "createPromotionCoupon"
  | "getCouponDetail"
  | "createPromotionCouponWithExistingCondition"
  | "updatePromotionCoupon"
  | "updatePromotionCondition"
  | "checkConflict"
  | "getTicketsByEventIds";

type CheckinKeyPath =
  | "eventResource"
  | "eventDropdown"
  | "eventList"
  | "eventDetail"
  | "eventDisplay"
  | "eventUpdate"
  | "eventHistory"
  | "sportParticipantResource"
  | "sportParticipantList"
  | "sportParticipantDetail"
  | "sportParticipantIds"
  | "sportParticipantEvents"
  | "sportParticipantCategories"
  | "sportParticipantCheckPos"
  | "sportParticipantClearPos"
  | "sportParticipantUpdate"
  | "sportParticipantDelete"
  | "sportParticipantExport"
  | "sportParticipantExportForPartner"
  | "sportParticipantExportResultResource"
  | "sportParticipantExportResult"
  | "sportParticipantHistory"
  | "attractionParticipantResource"
  | "attractionParticipantList"
  | "attractionParticipantDetail"
  | "attractionParticipantIds"
  | "attractionParticipantEvents"
  | "attractionParticipantCategories"
  | "attractionParticipantCheckPos"
  | "attractionParticipantUpdate"
  | "attractionParticipantDelete"
  | "attractionParticipantExport"
  | "attractionParticipantExportForPartner"
  | "attractionParticipantExportResultResource"
  | "attractionParticipantExportResult"
  | "attractionParticipantHistory"
  | "adminSportParticipantList"
  | "adminAttractionParticipantList"
  | "adminParticipantDetail"
  | "adminAttractionParticipantDetail"
  | "adminParticipantIds"
  | "adminEventDropdown"
  | "adminCategoryDropdown"
  | "adminSportParticipantExport"
  | "adminAttractionParticipantExport"
  | "adminExportResult"
  | "adminParticipantDelete"
  | "adminSportParticipantHistory"
  | "adminAttractionParticipantHistory";

export interface EndpointConfig {
  admin: string;
  wps: string;
}

export interface EndpointMap {
  [key: string]: EndpointConfig;
}

export interface EndpointParams {
  [key: string]: string | number;
}

class EndpointService {
  private eventEndpoints: EndpointMap = {
    // EVENT INFO
    categories: {
      admin: "content/dropdown/categories",
      wps: "events/categories/dropdown",
    },

    createEventInfo: {
      admin: "content/events/sports",
      wps: "events/sports",
    },

    updateEventInfo: {
      admin: "content/events",
      wps: "events/sports",
    },

    // ATTENDANT TEMPLATE
    getTemplates: {
      admin: "/content/attendant-templates/{eventId}/events",
      wps: "/events/{eventId}/attendant-templates",
    },

    getEventDropdown: {
      admin: "content/dropdown/events?v=2",
      wps: "events",
    },

    getAttendantTemplateByTemplateIds: {
      admin: "content/attendant-templates?ids={ids}",
      wps: "events/attendant-templates/by-ids?ids={ids}",
    },

    getParticipantAttendantTemplate: {
      admin: "participant/events/{eventId}/attendant-templates/{templateId}",
      wps: "participant/events/{eventId}/attendant-templates/{templateId}",
    },

    createTemplate: {
      admin: "content/attendant-templates",
      wps: "events/attendant-templates",
    },

    updateTemplate: {
      admin: "content/attendant-templates/{templateId}/update",
      wps: "events/attendant-templates/{templateId}",
    },

    getTemplateLog: {
      admin: "content/attendant-templates/{logId}/history",
      wps: "events/attendant-templates/{logId}/history",
    },

    // TICKETS AND PHASES
    getTickets: {
      admin: "content/events/{eventId}/tickets", // content/events GET
      wps: "events/{eventId}/tickets",
    },

    getPhases: {
      admin: "content/events/{eventId}/ticket-phases", // content/events GET
      wps: "events/{eventId}/ticket-phases",
    },

    toggleTicketPhaseStatus: {
      admin: "content/events/{eventId}/ticket-phase/{status}", // content/events UPDATE
      wps: "events/{eventId}/ticket-phases/{status}",
    },

    toggleGroupTicketStatus: {
      admin: "content/events/{eventId}/group-ticket/{status}", // content/events UPDATE
      wps: "events/{eventId}/group-tickets/{status}",
    },

    togglePublishPhasesStatus: {
      admin: "content/events/{eventId}/public-phases/{ticketType}/{status}", // content/events UPDATE
      wps: "events/{eventId}/public-phases/{ticketType}/{status}",
    },

    createTicketPhase: {
      admin: "content/ticket-phases",
      wps: "events/ticket-phases",
    },

    updateTicketPhase: {
      admin: "content/ticket-phases/{id}",
      wps: "events/ticket-phases/{id}",
    },

    createParentTicket: {
      admin: "content/tickets/parent",
      wps: "events/tickets/parent",
    },

    createSingleTicket: {
      admin: "content/tickets/single",
      wps: "events/tickets/single",
    },

    createTicketByType: {
      admin: "content/tickets/{type}",
      wps: "events/tickets/{type}",
    },

    updateParentTicket: {
      admin: "content/tickets/parent/{id}",
      wps: "events/tickets/parent/{id}",
    },

    updateSingleTicket: {
      admin: "content/tickets/single/{id}",
      wps: "events/tickets/single/{id}",
    },

    reorderTicket: {
      admin: "content/tickets/reorder",
      wps: "events/tickets/reorder",
    },

    createSubTicket: {
      admin: "content/tickets/sub",
      wps: "events/tickets/sub",
    },

    updateSubTicket: {
      admin: "content/tickets/sub/{id}",
      wps: "events/tickets/sub/{id}",
    },

    getTicketHistoryLog: {
      admin: "content/tickets/{logId}/history",
      wps: "events/tickets/{logId}/history",
    },

    getTicketPhaseHistoryLog: {
      admin: "content/ticket-phases/{logId}/history",
      wps: "events/ticket-phases/{logId}/history",
    },

    // ADITIONAL SETUP
    getAdditionalSetupData: {
      admin: "content/events/{eventId}/additional",
      wps: "events/sports/{eventId}/additional",
    },

    createAdditionalSetup: {
      admin: "content/events/sports/additional",
      wps: "events/sports/{eventId}/additional",
    },

    updateAdditionalSetup: {
      admin: "content/events/sports/additional",
      wps: "events/sports/{eventId}/additional",
    },

    //OTHERS
    getAttendantTemplateByEventId: {
      admin: "content/attendant-templates/events/{eventId}",
      wps: "events/{eventId}/attendant-templates/dropdown",
    },
  };

  private promotionEndpoints: EndpointMap = {
    getConditionDropdown: {
      admin: "promotion/events/{eventId}/conditions/dropdown?exclude_special=1",
      wps: "promotion/events/{eventId}/conditions/dropdown?exclude_special=1",
    },
    getConditionDropdown2: {
      admin: "promotion/conditions/dropdown?event_id={eventId}",
      wps: "promotion/events/{eventId}/conditions/dropdown",
    },
    getConditionDetail: {
      admin: "promotion/conditions/{conditionId}",
      wps: "promotion/conditions/{conditionId}",
    },
    getPromotionEventPercentFixedRuleDropdown: {
      admin: "promotion/events/percent-fixed-rules/dropdown",
      wps: "promotion/events/percent-fixed-rules/dropdown",
    },
    getPromotionEventDropdown: {
      admin: "promotion/events/dropdown?v=2",
      wps: "promotion/events/dropdown?v=2",
    },
    createPromotionCoupon: {
      admin: "promotion/",
      wps: "promotion/conditions",
    },
    createPromotionCouponWithExistingCondition: {
      admin: "promotion/coupons",
      wps: "promotion/coupons",
    },
    getCouponDetail: {
      admin: "promotion/coupons/{couponId}",
      wps: "promotion/coupons/{couponId}",
    },
    updatePromotionCoupon: {
      admin: "promotion/coupons/{couponId}",
      wps: "promotion/coupons/{couponId}",
    },
    updatePromotionCondition: {
      admin: "promotion/conditions/{conditionId}",
      wps: "promotion/conditions/{conditionId}",
    },
    checkConflict: {
      admin: "promotion/conditions/{conditionId}/check-conflicts",
      wps: ""
    },
    getTicketsByEventIds: {
      admin: "content/dropdown/tickets-by-event-ids?event_ids={eventIds}&product_type={productType}",
      wps: "events/tickets/by-event-ids?event_ids={eventIds}&product_type={productType}"
    }
  };

  private checkinEndpoints: EndpointMap = {
    eventResource: {
      admin: "pickup/events",
      wps: "pickup/events",
    },
    eventDropdown: {
      admin: "pickup/events/dropdown",
      wps: "pickup/events/dropdown",
    },
    eventList: {
      admin: "pickup/events/page/{page}",
      wps: "pickup/events/page/{page}",
    },
    eventDetail: {
      admin: "pickup/events/{id}",
      wps: "pickup/events/{id}",
    },
    eventDisplay: {
      admin: "pickup/events/{id}/display",
      wps: "pickup/events/{id}/display",
    },
    eventUpdate: {
      admin: "pickup/events/{id}",
      wps: "pickup/events/{id}",
    },
    eventHistory: {
      admin: "pickup/events/{id}/history",
      wps: "pickup/events/{id}/history",
    },
    sportParticipantResource: {
      admin: "pickup/sport-participants",
      wps: "pickup/sport-participants",
    },
    sportParticipantList: {
      admin: "pickup/sport-participants/page/{page}",
      wps: "pickup/sport-participants/page/{page}",
    },
    sportParticipantDetail: {
      admin: "pickup/sport-participants/{id}",
      wps: "pickup/sport-participants/{id}",
    },
    sportParticipantIds: {
      admin: "pickup/sport-participants/ids",
      wps: "pickup/sport-participants/ids",
    },
    sportParticipantEvents: {
      admin: "pickup/sport-participants/events/dropdown",
      wps: "pickup/sport-participants/events/dropdown",
    },
    sportParticipantCategories: {
      admin: "events/{eventId}/categories",
      wps: "events/{eventId}/categories",
    },
    sportParticipantCheckPos: {
      admin: "pickup/sport-participants/{participantId}/check-pos",
      wps: "pickup/sport-participants/{participantId}/check-pos",
    },
    sportParticipantClearPos: {
      admin: "pickup/sport-participants/{participantId}/pos123/clear",
      wps: "pickup/sport-participants/{participantId}/pos123/clear",
    },
    sportParticipantUpdate: {
      admin: "pickup/sport-participants/{id}",
      wps: "pickup/sport-participants/{id}",
    },
    sportParticipantDelete: {
      admin: "pickup/sport-participants/delete",
      wps: "pickup/sport-participants/delete",
    },
    sportParticipantExport: {
      admin: "pickup/sport-participants/export",
      wps: "pickup/sport-participants/export",
    },
    sportParticipantExportForPartner: {
      admin: "pickup/sport-participants/export-for-partner",
      wps: "pickup/sport-participants/export-for-partner",
    },
    sportParticipantExportResultResource: {
      admin: "pickup/sport-participants/export-result",
      wps: "pickup/sport-participants/export-result",
    },
    sportParticipantExportResult: {
      admin: "pickup/sport-participants/export-result/{id}",
      wps: "pickup/sport-participants/export-result/{id}",
    },
    sportParticipantHistory: {
      admin: "pickup/sport-participants/{participantId}/history",
      wps: "pickup/sport-participants/{participantId}/history",
    },
    attractionParticipantResource: {
      admin: "pickup/attraction-participants",
      wps: "pickup/attraction-participants",
    },
    attractionParticipantList: {
      admin: "pickup/attraction-participants/page/{page}",
      wps: "pickup/attraction-participants/page/{page}",
    },
    attractionParticipantDetail: {
      admin: "pickup/attraction-participants/{id}",
      wps: "pickup/attraction-participants/{id}",
    },
    attractionParticipantIds: {
      admin: "pickup/attraction-participants/ids",
      wps: "pickup/attraction-participants/ids",
    },
    attractionParticipantEvents: {
      admin: "pickup/attraction-participants/events/dropdown",
      wps: "pickup/attraction-participants/events/dropdown",
    },
    attractionParticipantCategories: {
      admin: "events/{eventId}/categories",
      wps: "events/{eventId}/categories",
    },
    attractionParticipantCheckPos: {
      admin: "pickup/attraction-participants/{participantId}/check-pos",
      wps: "pickup/attraction-participants/{participantId}/check-pos",
    },
    attractionParticipantUpdate: {
      admin: "pickup/attraction-participants/{id}",
      wps: "pickup/attraction-participants/{id}",
    },
    attractionParticipantDelete: {
      admin: "pickup/attraction-participants/delete",
      wps: "pickup/attraction-participants/delete",
    },
    attractionParticipantExport: {
      admin: "pickup/attraction-participants/export",
      wps: "pickup/attraction-participants/export",
    },
    attractionParticipantExportForPartner: {
      admin: "pickup/attraction-participants/export-for-partner",
      wps: "pickup/attraction-participants/export-for-partner",
    },
    attractionParticipantExportResultResource: {
      admin: "pickup/attraction-participants/export-result",
      wps: "pickup/attraction-participants/export-result",
    },
    attractionParticipantExportResult: {
      admin: "pickup/attraction-participants/export-result/{id}",
      wps: "pickup/attraction-participants/export-result/{id}",
    },
    attractionParticipantHistory: {
      admin: "pickup/attraction-participants/{participantId}/history",
      wps: "pickup/attraction-participants/{participantId}/history",
    },
    adminSportParticipantList: {
      admin: "pickup/participants",
      wps: "pickup/participants",
    },
    adminAttractionParticipantList: {
      admin: "pickup/participants/attractions",
      wps: "pickup/participants/attractions",
    },
    adminParticipantDetail: {
      admin: "pickup/participants",
      wps: "pickup/participants",
    },
    adminAttractionParticipantDetail: {
      admin: "pickup/participants/attractions",
      wps: "pickup/participants/attractions",
    },
    adminParticipantIds: {
      admin: "pickup/participants/ids",
      wps: "pickup/participants/ids",
    },
    adminEventDropdown: {
      admin: "content/dropdown/events?uncheck=true&event_type={eventType}",
      wps: "content/dropdown/events?uncheck=true&event_type={eventType}",
    },
    adminCategoryDropdown: {
      admin: "pickup/events/{eventId}/categories/dropdown",
      wps: "pickup/events/{eventId}/categories/dropdown",
    },
    adminSportParticipantExport: {
      admin: "export/pickup-sport-participants",
      wps: "export/pickup-sport-participants",
    },
    adminAttractionParticipantExport: {
      admin: "export/pickup-attraction-participants",
      wps: "export/pickup-attraction-participants",
    },
    adminExportResult: {
      admin: "export/result",
      wps: "export/result",
    },
    adminParticipantDelete: {
      admin: "pickup/participants",
      wps: "pickup/participants",
    },
    adminSportParticipantHistory: {
      admin: "pickup/participants/{participantId}/history",
      wps: "pickup/participants/{participantId}/history",
    },
    adminAttractionParticipantHistory: {
      admin: "pickup/participants/attractions/{participantId}/history",
      wps: "pickup/participants/attractions/{participantId}/history",
    },
  };

  private replaceUrlParams(url: string, params: EndpointParams): string {
    let result = url;

    Object.entries(params).forEach(([key, value]) => {
      const placeholder = `{${key}}`;
      result = result.replace(new RegExp(placeholder, "g"), String(value));
    });

    // Kiểm tra xem còn placeholder nào chưa được thay thế không
    const remainingPlaceholders = result.match(/\{[^}]+\}/g);
    if (remainingPlaceholders) {
      throw new Error(
        `Missing parameters: ${remainingPlaceholders.join(", ")}`
      );
    }

    return result;
  }

  getPromotionEndPoint(
    keyPath: PromotionKeyPath,
    params?: EndpointParams
  ): string {
    const user = JSON.parse(localStorage.getItem("USER") ?? "{}");
    const userType: UserType = user?.type;
    if (!userType) return "";
    const endpointConfig = this.promotionEndpoints[keyPath];

    if (!endpointConfig) {
      throw new Error(`Endpoint not found for key: ${keyPath}`);
    }

    let endpoint = endpointConfig[userType];

    // Nếu có parameters, thay thế chúng trong URL
    if (params) {
      endpoint = this.replaceUrlParams(endpoint, params);
    }

    return endpoint;
  }

  getEventEndpoint(keyPath: EventKeyPath, params?: EndpointParams): string {
    const user = JSON.parse(localStorage.getItem("USER") ?? "{}");
    const userType: UserType = user?.type;
    if (!userType) return "";
    const endpointConfig = this.eventEndpoints[keyPath];

    if (!endpointConfig) {
      throw new Error(`Endpoint not found for key: ${keyPath}`);
    }

    let endpoint = endpointConfig[userType];

    // Nếu có parameters, thay thế chúng trong URL
    if (params) {
      endpoint = this.replaceUrlParams(endpoint, params);
    }

    return endpoint;
  }

  getCheckinEndpoint(keyPath: CheckinKeyPath, params?: EndpointParams): string {
    const user = JSON.parse(localStorage.getItem("USER") ?? "{}");
    const userType: UserType = user?.type;
    if (!userType) return "";
    const endpointConfig = this.checkinEndpoints[keyPath];

    if (!endpointConfig) {
      throw new Error(`Endpoint not found for key: ${keyPath}`);
    }

    let endpoint = endpointConfig[userType];

    if (params) {
      endpoint = this.replaceUrlParams(endpoint, params);
    }

    return endpoint;
  }
}

export const endpointService = new EndpointService();

export const useEndpointService = () => {
  const getEventEndpoint = (
    keyPath: EventKeyPath,
    params?: EndpointParams
  ): string => {
    return endpointService.getEventEndpoint(keyPath, params);
  };

  return {
    getEventEndpoint,
    getCheckinEndpoint: (
      keyPath: CheckinKeyPath,
      params?: EndpointParams
    ): string => endpointService.getCheckinEndpoint(keyPath, params),
  };
};
